'use client'

import { Check } from 'lucide-react'
import { cn } from '@/utilities/ui'

/**
 * Wilson rich selection card — Figma `Rich Radio Group` 2198:39256 and
 * `Rich Checkbox Group` 2198:36391. ONE atom, one spec: both the appraisal wizard and
 * the offer-to-purchase wizard import this (the latter used to declare a private copy,
 * which had already drifted).
 *
 * Verified against the component sets 2026-07-22 (get_variable_defs + 1:1 renders):
 *   Surface    SOLID fill, NO border.
 *              resting  = `theme/muted`     #e0dfe0
 *              selected = `theme/secondary` #74cdde
 *              (neither component's variable set contains `theme/accent` #8cc640 at all)
 *   Radius     `sm` = 2px — not 8px.
 *   Box        `padding/p-3,5` 14px, `gap/gap-3` 12px between indicator and label stack,
 *              `gap/gap-2` 8px between label and secondary text, `shadow/xs`.
 *              Label-only card measures 44px tall (14 + 16 + 14) — matches Figma's
 *              `Rich Radio Group` instances on 4097:106167 (348 x 44).
 *   Label      `Body/bodySmAlt` — Lato **400**, 14/16, `theme/foreground`.
 *   Secondary  `Body/bodySm`    — Lato 300, 14/16, `theme/foreground`.
 *   Radio      16px circle, `theme/background` fill, 1px `theme/border`, shadow/xs;
 *              selected adds an 8px `theme/primary` (#000) dot.
 *   Checkbox   16px square radius `sm`, `theme/background` fill, 1px `theme/border`,
 *              shadow/xs; selected = `theme/primary` fill + 2px `theme/primary` border
 *              and a 14px `icon/icon-secondary` (#fff) check — identical to the
 *              canonical `src/components/ui/checkbox.tsx`, reproduced here as a
 *              presentational span because a Radix Root button cannot nest inside the
 *              card's own <button>.
 */
interface RadioCardProps {
  label: string
  subLabel?: string
  selected: boolean
  onSelect: () => void
  indicator?: 'radio' | 'checkbox'
  className?: string
}

export function RadioCard({
  label,
  subLabel,
  selected,
  onSelect,
  indicator = 'radio',
  className,
}: RadioCardProps) {
  const isCheckbox = indicator === 'checkbox'

  return (
    <button
      type="button"
      role={isCheckbox ? 'checkbox' : 'radio'}
      aria-checked={selected}
      onClick={onSelect}
      data-visual="rich-selection-card"
      className={cn(
        'flex w-full items-start gap-3 rounded-sm p-3.5 text-left shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
        selected ? 'bg-secondary' : 'bg-muted',
        className,
      )}
    >
      {isCheckbox ? (
        <span
          aria-hidden="true"
          className={cn(
            'flex size-4 shrink-0 items-center justify-center rounded-sm shadow-xs transition-colors',
            selected
              ? 'border-2 border-primary bg-primary text-primary-foreground'
              : 'border border-border bg-background',
          )}
        >
          {selected && <Check className="size-3.5" />}
        </span>
      ) : (
        <span
          aria-hidden="true"
          className="flex size-4 shrink-0 items-center justify-center rounded-full border border-border bg-background shadow-xs"
        >
          {selected && <span className="size-2 rounded-full bg-primary" />}
        </span>
      )}
      <span className="flex flex-col gap-2">
        <span className="text-[length:var(--text-body-sm)] leading-4 font-normal text-foreground">
          {label}
        </span>
        {subLabel && (
          <span className="text-[length:var(--text-body-sm)] leading-4 font-light text-foreground">
            {subLabel}
          </span>
        )}
      </span>
    </button>
  )
}
